const normalizeKey = (value) => String(value || '').trim().toLowerCase();

const WORKSPACE_TAB_ALIASES = {
    ledger: 'glentries',
};

export const normalizeWorkspaceTab = (tab) => {
    const requestedTab = String(tab || '').trim();
    return WORKSPACE_TAB_ALIASES[requestedTab] || requestedTab;
};

export const getWorkspaceTabFromSearch = (search, validTabs) => {
    const requestedTab = normalizeWorkspaceTab(new URLSearchParams(search || '').get('tab'));
    return requestedTab && validTabs?.has(requestedTab) ? requestedTab : 'home';
};

export const buildWorkspaceTabUrl = ({ pathname, search, hash, tab, fallbackCourseId }) => {
    const params = new URLSearchParams(search || '');
    if (fallbackCourseId && !params.get('course_id')) params.set('course_id', fallbackCourseId);
    const normalizedTab = normalizeWorkspaceTab(tab);
    if (!normalizedTab || normalizedTab === 'home') {
        params.delete('tab');
    } else {
        params.set('tab', normalizedTab);
    }

    const nextSearch = params.toString();
    return `${pathname || '/workspace'}${nextSearch ? `?${nextSearch}` : ''}${hash || ''}`;
};

export const resolveWorkspaceCourseId = ({ courses, current, search, role }) => {
    const requested = new URLSearchParams(search || '').get('course_id');
    if (requested && (courses.some(course => course.id === requested) || role === 'admin' || role === 'auditor')) {
        return requested;
    }
    if (courses.some(course => course.id === current)) return current;
    return courses[0]?.id || '';
};

export const buildWorkspaceCourseUrl = ({ pathname, search, hash, courseId, studentId }) => {
    const params = new URLSearchParams(search || '');
    // An intentional course change must not replay a source from the old course.
    ['course_code', 'course_title', 'student_id', 'student_name', 'student_email',
        'open_source', 'source_id', 'ref_no', 'source_type', 'source_label', 'source_origin']
        .forEach(key => params.delete(key));
    params.set('course_id', courseId);
    if (studentId) params.set('student_id', studentId);
    return `${pathname || '/workspace'}?${params.toString()}${hash || ''}`;
};

export const shouldReplaceWorkspaceTabNavigation = ({ search, validTabs }) => {
    const requestedTab = String(new URLSearchParams(search || '').get('tab') || '').trim();
    if (!requestedTab) return false;

    const normalizedTab = normalizeWorkspaceTab(requestedTab);
    return normalizedTab !== requestedTab || !validTabs?.has(normalizedTab);
};

export const buildNormalBalanceLookup = (accounts = []) => {
    const lookup = new Map();
    const debitTypes = new Set(['asset', 'contra-liability', 'expense', 'drawing']);
    const creditTypes = new Set(['liability', 'contra-asset', 'equity', 'revenue', 'income']);
    const visit = (account, inheritedNormalBalance = '') => {
        if (!account || typeof account !== 'object') return;
        const explicitNormalBalance = String(account.normal_balance || '').trim();
        const accountType = normalizeKey(account.type || account.account_type);
        const typeNormalBalance = debitTypes.has(accountType)
            ? 'Dr'
            : creditTypes.has(accountType)
                ? 'Cr'
                : '';
        const normalBalance = (
            (explicitNormalBalance === 'Dr' || explicitNormalBalance === 'Cr')
                ? explicitNormalBalance
                : typeNormalBalance || inheritedNormalBalance
        );
        if (normalBalance === 'Dr' || normalBalance === 'Cr') {
            const code = normalizeKey(account.code || account.account_code);
            const title = normalizeKey(account.title || account.account_name || account.name);
            if (code) lookup.set(`code:${code}`, normalBalance);
            if (title) lookup.set(`title:${title}`, normalBalance);
        }
        (account.subs || account.children || []).forEach(child => visit(child, normalBalance));
    };

    accounts.forEach(visit);
    return lookup;
};

export const getLedgerBalanceToneClass = ({
    balance,
    accountCode,
    accountTitle,
    normalBalanceLookup,
}) => {
    const amount = Number(balance || 0);
    if (Math.abs(amount) < 0.005) return 'text-slate-600';

    const code = normalizeKey(accountCode);
    const title = normalizeKey(accountTitle);
    const normalBalance = (
        (code && normalBalanceLookup?.get(`code:${code}`))
        || (title && normalBalanceLookup?.get(`title:${title}`))
        || ''
    );

    if (normalBalance !== 'Dr' && normalBalance !== 'Cr') return 'text-slate-600';
    const actualBalance = amount < 0 ? 'Cr' : 'Dr';
    return actualBalance === normalBalance ? 'text-emerald-600' : 'text-amber-600';
};
